import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
    FormHelperText,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Send as SendIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Warning as WarningIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import notificationService from '../../services/notificationService';
import userService from '../../services/userServices';
import roleService from '../../services/roleService';
import api from '../../services/api';

/**
 * Composant pour tester l'envoi de notifications
 */
const NotificationTestPanel = () => {
    const [notification, setNotification] = useState({
        title: '',
        message: '',
        type: 'info',
        category: 'system',
        link: ''
    });

    const [targetType, setTargetType] = useState('self');
    const [targetRole, setTargetRole] = useState('');
    const [targetUser, setTargetUser] = useState('');

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userLoadError, setUserLoadError] = useState(null);

    // Charger les rôles disponibles
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                // Utiliser roleService si disponible, sinon fallback sur l'API
                let rolesData = [];
                try {
                    rolesData = await roleService.getAllRoles();
                } catch (roleServiceErr) {
                    console.warn('Erreur avec roleService, fallback sur API:', roleServiceErr);
                    const response = await api.get('/roles');
                    if (response.data && response.data.success) {
                        rolesData = response.data.roles || [];
                    }
                }
                setRoles(rolesData);
            } catch (err) {
                console.error('Error fetching roles:', err);
                setRoles([]);
            }
        };

        fetchRoles();
    }, []);

    // Charger les utilisateurs lorsque targetType est 'user'
    useEffect(() => {
        if (targetType === 'user') {
            fetchUsers();
        }
    }, [targetType]);

    /**
     * Charger la liste des utilisateurs en utilisant userService
     * Méthode inspirée de UserManagement.jsx
     */
    const fetchUsers = async () => {
        try {
            setLoadingUsers(true);
            setUserLoadError(null);

            console.log('Chargement des utilisateurs via userService...');

            // Utiliser userService.getAllUsers() comme dans UserManagement.jsx
            const data = await userService.getAllUsers();
            console.log('Utilisateurs chargés:', data);

            if (data && Array.isArray(data)) {
                setUsers(data);
                console.log(`${data.length} utilisateurs chargés avec succès`);
            } else {
                console.warn('Format de données inattendu:', data);
                setUserLoadError('Format de données inattendu');
                setUsers([]);
            }
        } catch (err) {
            console.error('Erreur lors du chargement des utilisateurs:', err);
            setUserLoadError(err.message || 'Erreur lors du chargement des utilisateurs');
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    /**
     * Mettre à jour les champs du formulaire
     */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setNotification(prev => ({
            ...prev,
            [name]: value
        }));
    };

    /**
     * Envoyer la notification de test
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!notification.title || !notification.message) {
            setError('Le titre et le message sont requis');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let response;

            switch (targetType) {
                case 'self':
                    // Envoyer à soi-même
                    try {
                        response = await notificationService.sendTestNotification(notification);
                        // Vérifier si la réponse est valide
                        if (response && typeof response === 'object') {
                            if (response.success === true) {
                                setSuccess(true);
                                // Réinitialiser le formulaire pour le message uniquement
                                setNotification(prev => ({
                                    ...prev,
                                    message: ''
                                }));
                            } else {
                                // La réponse existe mais success n'est pas true
                                setError(response.message || 'Erreur lors de l\'envoi de la notification');
                            }
                        } else {
                            // La réponse n'est pas un objet valide
                            setError('Réponse invalide du service de notification');
                            console.error('Invalid response from notification service:', response);
                        }
                    } catch (err) {
                        console.error('Error in sendTestNotification:', err);
                        setError(err.message || 'Erreur lors de l\'envoi de la notification');
                    }
                    break;

                case 'user':
                    // Envoyer à un utilisateur spécifique
                    if (!targetUser) {
                        setError('Veuillez sélectionner un utilisateur');
                        setLoading(false);
                        return;
                    }
                    try {
                        response = await api.post(`/admin/notifications/send-to-user/${targetUser}`, notification);
                        if (response && response.data && response.data.success) {
                            setSuccess(true);
                            // Réinitialiser le formulaire pour le message uniquement
                            setNotification(prev => ({
                                ...prev,
                                message: ''
                            }));
                        } else {
                            setError((response && response.data && response.data.message) || 'Erreur lors de l\'envoi de la notification');
                        }
                    } catch (err) {
                        console.error('Error sending notification to user:', err);
                        setError(err.response?.data?.message || err.message || 'Erreur lors de l\'envoi de la notification');
                    }
                    break;

                case 'role':
                    // Envoyer à tous les utilisateurs d'un rôle
                    if (!targetRole) {
                        setError('Veuillez sélectionner un rôle');
                        setLoading(false);
                        return;
                    }
                    try {
                        response = await api.post(`/admin/notifications/send-to-role/${targetRole}`, notification);
                        if (response && response.data && response.data.success) {
                            setSuccess(true);
                            // Réinitialiser le formulaire pour le message uniquement
                            setNotification(prev => ({
                                ...prev,
                                message: ''
                            }));
                        } else {
                            setError((response && response.data && response.data.message) || 'Erreur lors de l\'envoi de la notification');
                        }
                    } catch (err) {
                        console.error('Error sending notification to role:', err);
                        setError(err.response?.data?.message || err.message || 'Erreur lors de l\'envoi de la notification');
                    }
                    break;

                case 'all':
                    // Envoyer à tous les utilisateurs
                    try {
                        response = await api.post('/admin/notifications/send-to-all', notification);
                        if (response && response.data && response.data.success) {
                            setSuccess(true);
                            // Réinitialiser le formulaire pour le message uniquement
                            setNotification(prev => ({
                                ...prev,
                                message: ''
                            }));
                        } else {
                            setError((response && response.data && response.data.message) || 'Erreur lors de l\'envoi de la notification');
                        }
                    } catch (err) {
                        console.error('Error sending notification to all users:', err);
                        setError(err.response?.data?.message || err.message || 'Erreur lors de l\'envoi de la notification');
                    }
                    break;

                default:
                    setError('Type de cible invalide');
                    setLoading(false);
                    return;
            }
        } catch (err) {
            console.error('Error sending test notification:', err);
            setError(err.message || 'Erreur lors de l\'envoi de la notification');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fermer l'alerte de succès
     */
    const handleCloseSuccess = () => {
        setSuccess(false);
    };

    /**
     * Fermer l'alerte d'erreur
     */
    const handleCloseError = () => {
        setError(null);
    };

    /**
     * Obtenir l'icône en fonction du type de notification
     */
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <SuccessIcon color="success" />;
            case 'warning':
                return <WarningIcon color="warning" />;
            case 'error':
                return <ErrorIcon color="error" />;
            case 'info':
            default:
                return <InfoIcon color="info" />;
        }
    };

    // Bouton pour recharger manuellement les utilisateurs
    const handleRefreshUsers = () => {
        fetchUsers();
    };

    return (
        <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
                Tester l'envoi de notifications
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
                Utilisez ce formulaire pour tester l'envoi de notifications à différents destinataires.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Titre"
                            name="title"
                            value={notification.title}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            margin="normal"
                        />

                        <TextField
                            fullWidth
                            label="Message"
                            name="message"
                            value={notification.message}
                            onChange={handleChange}
                            required
                            multiline
                            rows={4}
                            variant="outlined"
                            margin="normal"
                        />

                        <TextField
                            fullWidth
                            label="Lien (optionnel)"
                            name="link"
                            value={notification.link}
                            onChange={handleChange}
                            variant="outlined"
                            margin="normal"
                            placeholder="ex: /dashboard"
                            helperText="URL relative ou absolue"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="notification-type-label">Type de notification</InputLabel>
                            <Select
                                labelId="notification-type-label"
                                id="notification-type"
                                name="type"
                                value={notification.type}
                                onChange={handleChange}
                                label="Type de notification"
                                startAdornment={getNotificationIcon(notification.type)}
                            >
                                <MenuItem value="info">Information</MenuItem>
                                <MenuItem value="success">Succès</MenuItem>
                                <MenuItem value="warning">Avertissement</MenuItem>
                                <MenuItem value="error">Erreur</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <InputLabel id="notification-category-label">Catégorie</InputLabel>
                            <Select
                                labelId="notification-category-label"
                                id="notification-category"
                                name="category"
                                value={notification.category}
                                onChange={handleChange}
                                label="Catégorie"
                            >
                                <MenuItem value="system">Système</MenuItem>
                                <MenuItem value="account">Compte</MenuItem>
                                <MenuItem value="security">Sécurité</MenuItem>
                                <MenuItem value="properties">Propriétés</MenuItem>
                                <MenuItem value="messages">Messages</MenuItem>
                            </Select>
                            <FormHelperText>
                                La catégorie permet aux utilisateurs de filtrer leurs notifications
                            </FormHelperText>
                        </FormControl>

                        <Accordion sx={{ mt: 2 }} defaultExpanded={targetType !== 'self'}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="target-content"
                                id="target-header"
                            >
                                <Typography>Destinataire</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="target-type-label">Envoyer à</InputLabel>
                                    <Select
                                        labelId="target-type-label"
                                        id="target-type"
                                        value={targetType}
                                        onChange={(e) => setTargetType(e.target.value)}
                                        label="Envoyer à"
                                    >
                                        <MenuItem value="self">Moi-même (test)</MenuItem>
                                        <MenuItem value="user">Utilisateur spécifique</MenuItem>
                                        <MenuItem value="role">Tous les utilisateurs d'un rôle</MenuItem>
                                        <MenuItem value="all">Tous les utilisateurs</MenuItem>
                                    </Select>
                                </FormControl>

                                {targetType === 'role' && (
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel id="target-role-label">Rôle</InputLabel>
                                        <Select
                                            labelId="target-role-label"
                                            id="target-role"
                                            value={targetRole}
                                            onChange={(e) => setTargetRole(e.target.value)}
                                            label="Rôle"
                                        >
                                            {roles.length === 0 ? (
                                                <MenuItem value="" disabled>
                                                    Aucun rôle disponible
                                                </MenuItem>
                                            ) : (
                                                roles.map(role => (
                                                    <MenuItem key={role.id} value={role.name}>
                                                        {role.displayName || role.name}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                )}

                                {targetType === 'user' && (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                            <Button
                                                size="small"
                                                onClick={handleRefreshUsers}
                                                disabled={loadingUsers}
                                                variant="outlined"
                                            >
                                                Actualiser la liste
                                            </Button>
                                        </Box>
                                        <FormControl fullWidth margin="normal" error={!!userLoadError}>
                                            <InputLabel id="target-user-label">Utilisateur</InputLabel>
                                            <Select
                                                labelId="target-user-label"
                                                id="target-user"
                                                value={targetUser}
                                                onChange={(e) => setTargetUser(e.target.value)}
                                                label="Utilisateur"
                                                disabled={loadingUsers}
                                            >
                                                {loadingUsers ? (
                                                    <MenuItem value="">
                                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                                        Chargement...
                                                    </MenuItem>
                                                ) : users.length === 0 ? (
                                                    <MenuItem value="" disabled>
                                                        {userLoadError || 'Aucun utilisateur disponible'}
                                                    </MenuItem>
                                                ) : (
                                                    users.map(user => (
                                                        <MenuItem key={user.id} value={user.id}>
                                                            {user.name || user.username || user.email}
                                                            {user.email && ` (${user.email})`}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                            {userLoadError && (
                                                <FormHelperText>{userLoadError}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                        disabled={loading}
                    >
                        {loading ? 'Envoi en cours...' : 'Envoyer la notification'}
                    </Button>
                </Box>
            </form>

            <Snackbar
                open={success}
                autoHideDuration={6000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
                    Notification envoyée avec succès !
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default NotificationTestPanel;